// src/lib/analytics/region.ts
/**
 * @fileoverview Consent-region geography — the single source of truth shared by
 * the client Consent Mode bootstrap, the edge middleware region cookie, and the
 * server-side checkout consent gate.
 *
 * Why this exists as its own module: it must be importable from THREE runtimes
 * with no `window`/Node coupling — the browser bundle (`consent-mode.ts`), the
 * Edge middleware (`middleware.ts`), and the Node server (`stripe/checkout.ts`).
 * Keeping it dependency-free (a frozen list + two pure predicates) lets each
 * pull it without dragging in the others' runtime assumptions.
 */

/**
 * ISO 3166-1 alpha-2 country codes whose visitors default to consent-DENIED
 * under Google Consent Mode v2: the EEA (EU-27 + Iceland, Liechtenstein,
 * Norway) plus the UK (GB) and Switzerland (CH). Consent is legally required
 * BEFORE non-essential storage / analytics processing for these visitors.
 */
export const CONSENT_DENIED_BY_DEFAULT_REGIONS: readonly string[] = [
  // EU-27
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR",
  "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK",
  "SI", "ES", "SE",
  // EEA non-EU
  "IS", "LI", "NO",
  // UK + Switzerland
  "GB", "CH",
]

/**
 * Cookie the Edge middleware sets ("1" = consent-required region, "0" = not) so
 * the CLIENT can tell — with NO network round-trip and WITHOUT the app/layout
 * ever reading geo itself (which would force dynamic rendering and disqualify
 * the root layout from bfcache) — whether it sits in a default-deny region. The
 * client's first-party attribution-storage gate reads it synchronously from
 * `document.cookie`. Deliberately NOT `HttpOnly` (client JS must read it) and
 * low-sensitivity (a coarse region flag, not PII).
 */
export const REGION_CONSENT_REQUIRED_COOKIE = "adsights_region_cr"

const DENIED_REGION_SET = new Set<string>(CONSENT_DENIED_BY_DEFAULT_REGIONS)

/**
 * True when an ISO 3166-1 alpha-2 country code is in the consent-required
 * (EEA/UK/CH) set. Case-insensitive. Returns `false` for null/undefined/unknown
 * (e.g. Vercel's `XX`) — i.e. it FAILS OPEN to the rest-of-world opt-out
 * default. That is the intended posture: Vercel reliably stamps
 * `x-vercel-ip-country` in production, so a real EEA visitor is detected; only
 * local dev / non-Vercel hosts (no header) fall through to opt-out, which is not
 * a legal-compliance environment.
 */
export function isConsentRequiredRegion(country: string | null | undefined): boolean {
  if (!country) return false
  return DENIED_REGION_SET.has(country.toUpperCase())
}
